import { Spinner, Text, XStack, YStack } from "tamagui";
import type { Product } from "@utils/store/cart/useOrder";
import { buildGasCouponDiscountMap, discountedLineTotal } from "@screen/Home/pages/GasShop/utils/couponDiscount";
import { formatOrderableGasTitle } from "@utils/func/gasTitle";
import type { KioskPreview } from "../api/kioskApi";
import { pointsToNtd, type PointsRedeemResult } from "@utils/points";
import { kiosk } from "../kioskTheme";
import KioskIcon from "../KioskIcon";
import { RiCoinFill } from "react-icons/ri";
import { SectionLabel } from "./ConfirmParts";
import { useKiosk } from "../useKiosk";

const Row = ({ k, v, muted }: { k: string; v: string; muted?: boolean }) => (
  <XStack jc="space-between" ai="center" py={3}>
    <Text fontSize={15} color={muted ? kiosk.grey : kiosk.ink}>
      {k}
    </Text>
    <Text fontSize={15} fontWeight="600" color={muted ? kiosk.grey : kiosk.ink}>
      {v}
    </Text>
  </XStack>
);

/** 品項列：有折扣時顯示原價（刪除線）+ 折後價，否則只顯示單一價格。 */
const ItemRow = ({ name, qty, original, discounted, hidePrice }: { name: string; qty: number; original: number; discounted: number; hidePrice?: boolean }) => {
  const hasDiscount = discounted < original;
  return (
    <XStack jc="space-between" ai="center" py={4} gap={10}>
      <Text fontSize={15} color={kiosk.ink} flexShrink={1}>
        {name} × {qty}
      </Text>
      {!hidePrice && (
        <XStack ai="center" gap={7} flexShrink={0}>
          {hasDiscount && (
            <Text fontSize={13} color={kiosk.grey} style={{ textDecorationLine: "line-through" }}>
              NT$ {original.toLocaleString()}
            </Text>
          )}
          <Text fontSize={17} fontWeight="800" color={kiosk.ink}>
            NT$ {discounted.toLocaleString()}
          </Text>
        </XStack>
      )}
    </XStack>
  );
};

/**
 * 訂單摘要（對齊訂購頁結算頁 GasOrderSummary）：
 * 逐項列「{公斤數}KG 瓦斯 × 數量」原價 + 折後價（有優惠券時原價加刪除線），
 * 接著小計（折後）/ 樓層費 / 其他費用，最後應付金額對齊 preview 權威總額，
 * 並在應付金額下方標示已套用優惠券與折抵金額。
 */
const KioskOrderSummary = ({
  preview,
  productList,
  count,
  total,
  loading,
  points,
}: {
  preview: KioskPreview | null;
  productList: Product[];
  count: number;
  /** 應付金額（已含點數折抵） */
  total: number;
  loading: boolean;
  /** 點數折抵結果；null = 這張單不適用點數 */
  points?: PointsRedeemResult | null;
}) => {
  const { hidePrice } = useKiosk();
  const floorFee = preview?.floor_fee ?? 0;
  const fee = preview?.fee ?? 0;
  const couponDiscount = preview?.coupon_discount ?? 0;
  const storedGasNtd = pointsToNtd(points?.storedGas ?? 0);
  const accountNtd = pointsToNtd(points?.account ?? 0);
  const pointsDiscount = storedGasNtd + accountNtd;
  const gasCouponDiscountMap = buildGasCouponDiscountMap(productList, couponDiscount);
  // 依公斤數排序（小→大）；非瓦斯排最後。折扣以 line_id 對應，排序不影響金額。
  const sorted = [...productList].sort((a, b) => (a.kilogram ?? Infinity) - (b.kilogram ?? Infinity));
  // 各品項折後價先算好，小計直接取其加總 → 保證「小計」恆等於上方逐項折後價之和
  //（折扣攤分在 map 內已 clamp 不超過瓦斯小計，避免邊界值與小計對不上）
  const lineTotals = sorted.map((it, idx) => discountedLineTotal(it, idx, gasCouponDiscountMap));
  const discountedSubtotal = lineTotals.reduce((sum, n) => sum + n, 0);

  return (
    <>
      <SectionLabel>訂單摘要</SectionLabel>
      <YStack backgroundColor={kiosk.card} borderWidth={1} borderColor={kiosk.line} borderRadius={16} px={16} py={12}>
        {/* 品項明細：原價 + 折後瓦斯價 */}
        {sorted.map((it, idx) => {
          const name = it.gas_type ? formatOrderableGasTitle(it.kilogram) : it.display_name || "商品";
          return <ItemRow key={it.line_id ?? idx} name={name} qty={it.qty} original={it.unit_price * it.qty} discounted={lineTotals[idx]} hidePrice={hidePrice} />;
        })}

        <YStack my={8} height={1} backgroundColor={kiosk.line} />

        {/* 小計 / 樓層費 / 其他（營業客戶不顯示價格明細） */}
        {!hidePrice && (
          <>
            <Row k="小計" v={`NT$ ${discountedSubtotal.toLocaleString()}`} muted />
            {floorFee > 0 && <Row k="樓層費" v={`NT$ ${floorFee.toLocaleString()}`} muted />}
            {fee > 0 && <Row k="其他費用" v={`NT$ ${fee.toLocaleString()}`} muted />}
            {storedGasNtd > 0 && <Row k={`存氣點數折抵（${points!.storedGas.toLocaleString()} 點）`} v={`−NT$ ${storedGasNtd.toLocaleString()}`} muted />}
            {accountNtd > 0 && <Row k={`帳戶點數折抵（${points!.account.toLocaleString()} 點）`} v={`−NT$ ${accountNtd.toLocaleString()}`} muted />}
          </>
        )}

        <XStack jc="space-between" ai="flex-start" mt={6} pt={11} borderTopWidth={1} borderColor={kiosk.line}>
          <Text fontSize={18} fontWeight="800" color={kiosk.ink}>
            應付金額（{count} 件）
          </Text>
          {hidePrice ? (
            <Text fontSize={16} fontWeight="700" color={kiosk.grey}>
              專員報價
            </Text>
          ) : loading ? (
            <Spinner size="small" color={kiosk.blue} />
          ) : (
            <YStack ai="flex-end" gap={3}>
              <Text className="kiosk-grad-text" fontSize={24} fontWeight="800" letterSpacing={-0.5}>
                NT$ {total.toLocaleString()}
              </Text>
              {/* 優惠券標示就跟在金額下方 */}
              {couponDiscount > 0 && (
                <XStack ai="center" gap={4}>
                  <KioskIcon name="ticket" size={13} color={kiosk.blue} />
                  <Text fontSize={12} fontWeight="700" color={kiosk.blue}>
                    已套用優惠券 −NT$ {couponDiscount.toLocaleString()}
                  </Text>
                </XStack>
              )}
              {pointsDiscount > 0 && (
                <XStack ai="center" gap={4}>
                  <RiCoinFill size={13} color={kiosk.green} />
                  <Text fontSize={12} fontWeight="700" color={kiosk.green}>
                    已折抵點數 −NT$ {pointsDiscount.toLocaleString()}
                  </Text>
                </XStack>
              )}
            </YStack>
          )}
        </XStack>
      </YStack>
    </>
  );
};

export default KioskOrderSummary;
